const HTML_TAG_PATTERN = /<\/?[a-z][\s\S]*>/i;

function escapeHtml(text: string): string {
  return text
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");
}

function normalizeNewlines(text: string): string {
  return text.replace(/\r\n/g, "\n").replace(/\r/g, "\n");
}

function linkifyEscaped(value: string): string {
  const withLabels = value.replace(
    /\[([^\]]+)\]\((https?:\/\/[^\s)]+)\)/g,
    (_match, label: string, href: string) => `<a href="${href}">${label}</a>`,
  );
  return withLabels.replace(/(?<!href=")https?:\/\/[^\s<]+/g, (url) => {
    const href = url.replace(/[),.]+$/g, "");
    const tail = url.slice(href.length);
    return `<a href="${href}">${href}</a>${tail}`;
  });
}

/** True when the stored template looks like HTML rather than a textarea draft. */
export function looksLikeHtml(value: string): boolean {
  return HTML_TAG_PATTERN.test(value);
}

/**
 * Turn a textarea draft into HTML so email clients keep paragraphs and line breaks.
 * Blank lines become <p> blocks; single newlines become <br />.
 */
export function plainTextToHtml(text: string): string {
  const escaped = linkifyEscaped(escapeHtml(normalizeNewlines(text)));
  const paragraphs = escaped
    .split(/\n{2,}/)
    .map((paragraph) => paragraph.replace(/\n/g, "<br />"))
    .filter((paragraph) => paragraph.length > 0);

  if (paragraphs.length === 0) {
    return "";
  }

  return paragraphs
    .map(
      (paragraph) =>
        `<p style="margin:0 0 16px 0;line-height:1.6;">${paragraph}</p>`,
    )
    .join("");
}

/** Convert stored HTML templates into an editable plain-text draft. */
export function htmlToPlainText(html: string): string {
  const normalized = normalizeNewlines(html);
  if (!looksLikeHtml(normalized)) {
    return normalized;
  }

  return normalized
    .replace(/<br\s*\/?>/gi, "\n")
    .replace(/<\/p>/gi, "\n\n")
    .replace(/<\/div>/gi, "\n")
    .replace(/<\/h[1-6]>/gi, "\n\n")
    .replace(/<\/li>/gi, "\n")
    .replace(/<\/tr>/gi, "\n")
    .replace(/<[^>]+>/g, "")
    .replace(/&nbsp;/gi, " ")
    .replace(/&amp;/g, "&")
    .replace(/&lt;/g, "<")
    .replace(/&gt;/g, ">")
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'")
    .replace(/[ \t]+\n/g, "\n")
    .replace(/\n{3,}/g, "\n\n")
    .trim();
}
